import { Fragment, useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import * as Material from "@mui/material";

import api from "@helpers/api";
import {
  POST_PARAM,
  communitySlugFor,
  heartPost,
  postShareLink,
  postStore,
} from "@helpers/posts";

import { useCommunity } from "contexts/community";

import AdminPostManageModal from "@components/_modals/Group/AdminPostManageModal";

import { ReactComponent as PostCreation } from "../../assets/draws/product-creation.svg";

import Post from "./components/Post";

import "./styles.scss";


const Feed = ({
  fitContentHeight = false,
  filterByUser = {},
  groupId,
  containerProps = {},
  fluidMode = false,
  // Managers opt in to their scheduled posts; the API ignores it for anyone else.
  includeScheduled = false,
}) => {
  const { t } = useTranslation("community");
  const [, setSearchParams] = useSearchParams();

  const [loadingFeed, setLoadingFeed] = useState(true);
  const [posts, setPosts] = useState([]);

  const adminPostManageModalRef = useRef();

  const { loading, community } = useCommunity();

  const isInSpecificUserMode = !!filterByUser?.id;
  const firstName = isInSpecificUserMode
    ? filterByUser.username?.split(" ")[0]
    : null;

  useEffect(() => {
    if (loadingFeed) loadFeed();
  }, [loadingFeed, filterByUser]);

  async function loadFeed() {
    setLoadingFeed(true);

    try {
      if (!groupId) {
        setPosts([]);
        setLoadingFeed(false);
        return;
      }

      const { data } = await api.get(
        `community/group/${groupId}/posts/list?page=1&per_page=15${
          includeScheduled ? "&include_scheduled=1" : ""
        }`
      );

      const list = Array.isArray(data?.data) ? data.data : [];
      list.forEach((post) => postStore.set(post));
      setPosts(list);
    } catch (e) {
      window.setSnackbar(t("feed.loadError"));
    }

    setLoadingFeed(false);
  }

  function handleManagePost(post) {
    adminPostManageModalRef.current?.open(post, () => setLoadingFeed(true));
  }

  // Opening a post = stamping `?post=<id>`: the community page owns the modal
  // and reads the param, so a shared link and a click land on the same path.
  // The store already holds the card's copy, so the modal opens instantly.
  function handleOpenPost(post) {
    postStore.set(post);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(POST_PARAM, post.id);
      return next;
    });
  }

  async function handleReact(post) {
    const done = await heartPost(post);
    if (!done) window.setSnackbar(t("feed.reactError"));
  }

  // Share = the post's deep link inside its community (`/:slug?post=<id>`),
  // through the app's share sheet.
  function handleShare(post) {
    const slug = communitySlugFor(post, (id) => community.get(id));
    window.handleOpenShareContentModal?.({
      title: t("feed.shareTitle"),
      link: postShareLink(slug, post.id),
    });
  }

  const NoPostsFound = () => {
    return (
      <div className="no-posts-found mt-3" {...containerProps}>
        <PostCreation className="no-posts-banner" />
        <h4 className="mb-1 mt-1">
          {isInSpecificUserMode
            ? t("feed.empty.userTitle", { name: firstName })
            : t("feed.empty.groupTitle")}
        </h4>
        <span>
          {t("feed.empty.subtitle")}
        </span>
      </div>
    );
  };

  const LoadingComponents = () => {
    return (
      <div className="loading-feed-container px-8">
        <Material.Skeleton height={80} className="mt-2" />

        <Material.Skeleton height={300} className="mt-2" />

        <Material.Skeleton height={300} className="mt-2" />
      </div>
    );
  };

  return (
    <div
      className={
        "feed-area " +
        (fitContentHeight && "fluid-feed-height") +
        (fluidMode && " fluid-mode")
      }
    >
      <AdminPostManageModal modalRef={adminPostManageModalRef} />
      <div className="feed-container">
        {loading || loadingFeed ? (
          <LoadingComponents />
        ) : (
          <>
            {posts?.length > 0 ? (
              posts.map((post, i) => {
                if (!post?.publisher) return null;
                const isAdmin = !!community.get(post.community?.id)?.isAdmin?.();

                return (
                  <Fragment key={post.id}>
                    {i !== 0 && <Material.Divider className="posts-divider mb-4" />}
                    <div className="screen-padding">
                      <Post
                        post={post}
                        isAdmin={isAdmin}
                        onOpen={handleOpenPost}
                        onManage={handleManagePost}
                        onReact={handleReact}
                        onShare={handleShare}
                      />
                    </div>
                  </Fragment>
                );
              })
            ) : (
              <NoPostsFound />
            )}
          </>
        )}

        <div style={{ height: "80px" }}></div>
      </div>
    </div>
  );
};

export default Feed;
